import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ENV } from '../env';
import {
  AUTH_CHANGE_EVENT,
  AuthRefreshError,
  decodeIdToken,
  ensureFreshTokens,
  getTokens,
  logout as doLogout,
  startLogin,
  type Tokens,
} from './auth';

export type AuthRole = 'member' | 'admin' | 'superadmin';

interface AuthState {
  tokens: Tokens | null;
  email: string | null;
  userId: string | null;
  groups: string[];
  role: AuthRole;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  login: () => Promise<void>;
  logout: () => void;
  /** Called by the callback page after successful token exchange. */
  refresh: () => void;
}

const Ctx = createContext<AuthState | null>(null);

function roleFromGroups(groups: readonly string[]): AuthRole {
  if (groups.includes('superadmin')) return 'superadmin';
  if (groups.includes('admin')) return 'admin';
  return 'member';
}

export const SILENT_REFRESH_RETRY_MS = { initial: 10_000, max: 5 * 60_000 };

function sameTokens(a: Tokens, b: Tokens): boolean {
  return a.idToken === b.idToken && a.accessToken === b.accessToken && a.expiresAt === b.expiresAt;
}

/**
 * Schedules the idle-tab refresh for `tokens` and returns a cancel function.
 * A refresh whose outcome is unknown (network/Cognito outage) leaves the
 * session alone and retries with capped backoff; an explicit rejection
 * clears the session inside ensureFreshTokens and is reported as null.
 */
export function startSilentRefresh(tokens: Tokens, onTokens: (next: Tokens | null) => void): () => void {
  let cancelled = false;
  let timer: number | undefined;
  let retryMs = SILENT_REFRESH_RETRY_MS.initial;

  const attempt = async () => {
    let fresh: Tokens | null;
    try {
      fresh = await ensureFreshTokens();
    } catch (err) {
      if (!(err instanceof AuthRefreshError)) throw err;
      if (cancelled) return;
      timer = window.setTimeout(attempt, retryMs);
      retryMs = Math.min(retryMs * 2, SILENT_REFRESH_RETRY_MS.max);
      return;
    }
    if (cancelled) return;
    const next = fresh ?? getTokens();
    // Unchanged tokens (e.g. no refresh token stored) would re-arm this
    // refresher with a zero delay and spin; leave state alone instead.
    if (next && sameTokens(next, tokens)) return;
    onTokens(next);
  };

  timer = window.setTimeout(attempt, Math.max(0, tokens.expiresAt - Date.now() - 60_000));
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [tokens, setTokens] = useState<Tokens | null>(() => getTokens());

  const refresh = useCallback(() => setTokens(getTokens()), []);
  const login = useCallback(
    () => startLogin(window.location.pathname + window.location.search),
    [],
  );

  // Re-read tokens if this tab returns to the foreground — user may have
  // logged in/out in another tab or localStorage may have changed while this
  // document was inactive. Cheap enough to run on every foreground signal.
  useEffect(() => {
    const syncTokens = () => setTokens(getTokens());
    const onVisibility = () => {
      if (document.visibilityState === 'visible') syncTokens();
    };
    window.addEventListener('focus', syncTokens, true);
    window.addEventListener('pageshow', syncTokens);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', syncTokens, true);
      window.removeEventListener('pageshow', syncTokens);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Cross-tab sync: if another tab refreshes (or logs out), pick up the
  // new token state without forcing the user to switch tabs.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key && !e.key.startsWith('starmap.')) return;
      setTokens(getTokens());
    };
    const onLocalAuthChange = () => setTokens(getTokens());
    window.addEventListener('storage', onStorage);
    window.addEventListener(AUTH_CHANGE_EVENT, onLocalAuthChange);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(AUTH_CHANGE_EVENT, onLocalAuthChange);
    };
  }, []);

  // Silent refresh — kick a swap a minute before expiry so the user never
  // sees a 401 mid-session. The API client also refreshes reactively, but
  // doing it here keeps idle tabs alive without waiting for a request.
  useEffect(() => (tokens ? startSilentRefresh(tokens, setTokens) : undefined), [tokens]);

  const claims = useMemo(
    () => (tokens ? decodeIdToken(tokens.idToken) : null),
    [tokens],
  );

  // Memoized so `auth`'s identity is stable across renders that don't change
  // auth state. Consumers (RequireAuth, AuthCallback) key effects off `auth`;
  // an unstable value made those effects re-fire — re-running login() (PKCE
  // verifier churn) and the single-use code exchange (invalid_grant).
  const value = useMemo<AuthState>(
    () => {
      const previewGroups = ENV.previewBypassAuth ? ['admin'] : [];
      const groups = claims?.['cognito:groups'] ?? previewGroups;
      const role = roleFromGroups(groups);

      return {
        tokens,
        email: claims?.email ?? (ENV.previewBypassAuth ? 'preview@caylent.com' : null),
        userId: claims?.sub ?? (ENV.previewBypassAuth ? 'starmap-preview' : null),
        groups,
        role,
        isAuthenticated: !!tokens || ENV.previewBypassAuth,
        isAdmin: role === 'admin' || role === 'superadmin',
        isSuperAdmin: role === 'superadmin',
        login,
        logout: doLogout,
        refresh,
      };
    },
    [tokens, claims, login, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}
